<script setup lang="ts">
import { FlagProvider } from "@unleash/proxy-client-vue";
import { UnleashClient } from "unleash-proxy-client";
import AddToHomeScreenHint from "./components/common/AddToHomeScreenHint.vue";
import NotificationBanner from "@persolog/ui/NotificationBanner.vue";
import ReleaseNotesPopup from "./components/common/ReleaseNotesPopup.vue";
import { computed } from "vue";
import { useNotificationBanners } from "@persolog/utils";
import { i18n, trainerDetails } from "./main";
import { LanguageManagementAPI } from "./api";

/**
 * Unleash client for feature flags
 */
const unleash = new UnleashClient({
    url: import.meta.env.VITE_APP_UNLEASH_API_BASE_URL,
    clientKey: import.meta.env.VITE_APP_UNLEASH_API_CLIENT_KEY,
    appName: import.meta.env.VITE_APP_UNLEASH_API_APP_NAME,
});

const useUnleash = import.meta.env.VITE_APP_USE_UNLEASH === "true";

if (useUnleash) {
    // Log only that the client started
    unleash.on("ready", () => {
        console.info("[Unleash] Feature flag client started");
    });

    unleash.start();
}

/**
 * Notification banners for this app (RDT-339). Language-management decides what is active and
 * inside its display window; the component picks the ones for this viewer's audience. A banner is
 * decoration, so a failure here leaves the app without one rather than breaking it.
 */
const interfaceLanguage = computed(() => i18n.global.locale.value);
const banners = useNotificationBanners(interfaceLanguage, LanguageManagementAPI.fetchBanners);
</script>

<template>
    <NotificationBanner :banners="banners" :locale="interfaceLanguage" :trainer="trainerDetails" />
    <!-- iOS has no automatic install prompt, so the home-screen hint is route-independent too (RDT-296). -->
    <AddToHomeScreenHint />
    <!--
      The popup is route-independent, so a returning trainer sees new features wherever they land
      (RDT-286) — but it sits *inside* the FlagProvider, because it reads its own Unleash flag.
    -->
    <FlagProvider v-if="useUnleash" :unleash-client="unleash">
        <ReleaseNotesPopup v-if="trainerDetails" :trainer="trainerDetails" />
        <router-view></router-view>
    </FlagProvider>
    <template v-else>
        <ReleaseNotesPopup v-if="trainerDetails" :trainer="trainerDetails" />
        <router-view></router-view>
    </template>
</template>
